import { useState } from "react";
import { useLocation, Link } from "wouter";
import { useLogin, useSelectCompany, getGetMeQueryKey, type LoginResponse, type Company } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ChevronRight, Loader2, CheckCircle2, KeyRound } from "lucide-react";
import { trackLogin, trackLoginFailed } from "@/lib/metrics";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [pickingCompany, setPickingCompany] = useState(false);
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const login = useLogin();
  const selectCompany = useSelectCompany();
  const qc = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result: LoginResponse = await login.mutateAsync({ data: { email, password } });
      trackLogin("email");
      if (result.redirect) {
        await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
        setLocation(result.redirect);
      } else if (result.companies && result.companies.length > 1) {
        setCompanies(result.companies);
        setPickingCompany(true);
      }
    } catch (err: unknown) {
      trackLoginFailed("email");
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: msg || "Invalid credentials. Try again.",
      });
    }
  };

  const handleSelectCompany = async (companyId: string) => {
    setSelectingId(companyId);
    try {
      const result = await selectCompany.mutateAsync({ data: { companyId } });
      await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
      setLocation((result as { redirect?: string }).redirect || "/cp");
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({
        variant: "destructive",
        title: "Error",
        description: msg || "Failed to select company.",
      });
      setSelectingId(null);
    }
  };

  const featureBullets = [
    "Real-time draw schedule management",
    "Automated lender & vendor workflows",
    "Compliance tracking & audit trails",
  ];

  if (pickingCompany) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 60%, #1a2a5a 100%)" }}>
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src="/images/finproject-logo.png" alt="FinProject" className="w-16 h-16 object-contain mb-4 rounded-xl" style={{ border: "2px solid #ffffff", padding: "3px", background: "#ffffff" }} />
            <h1 className="text-2xl font-bold text-white mb-1">Choose a company</h1>
            <p className="text-sm text-white/60 text-center">You're a member of multiple companies. Select one to continue.</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-sm p-6">
            <div className="space-y-3">
              {companies.map(co => (
                <button
                  key={co.id}
                  onClick={() => handleSelectCompany(co.id)}
                  disabled={!!selectingId}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/20 hover:border-amber-400/50 hover:bg-white/10 transition-all text-left group"
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0"
                    style={{ background: co.color || "#C9A840" }}
                  >
                    {co.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate">{co.name}</div>
                    <div className="text-sm text-white/60">{co.plan} · {co.status}</div>
                  </div>
                  {selectingId === co.id
                    ? <Loader2 className="w-4 h-4 animate-spin shrink-0 text-amber-400" />
                    : <ChevronRight className="w-5 h-5 shrink-0 text-white/40 group-hover:text-amber-400 transition-colors" />
                  }
                </button>
              ))}
            </div>
          </div>

          <Button variant="ghost" className="w-full mt-4 text-white/60 hover:text-white hover:bg-white/10" onClick={() => setPickingCompany(false)}>
            ← Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left branding panel */}
      <div
        className="hidden md:flex flex-col justify-center px-12 relative overflow-hidden"
        style={{
          flex: "0 0 45%",
          background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 60%, #1a2a5a 100%)",
        }}
      >
        <div className="relative z-10 max-w-sm mx-auto">
          <div className="mb-8">
            <img src="/images/finproject-logo.png" alt="FinProject" className="w-24 h-24 object-contain rounded-2xl" style={{ border: "2px solid #ffffff", padding: "4px", background: "#ffffff" }} />
          </div>

          <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">FinProject</h2>
          <p className="text-lg font-medium mb-10" style={{ color: "#C9A840" }}>Construction Finance Platform</p>

          <div className="space-y-5">
            {featureBullets.map((bullet, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "#C9A840" }} />
                <span className="text-sm leading-relaxed text-white/80">{bullet}</span>
              </div>
            ))}
          </div>

          <div className="mt-14 pt-8 border-t border-white/10">
            <p className="text-xs text-white/30">Trusted by construction finance teams nationwide</p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 60%, #1a2a5a 100%)" }}>
        <div className="w-full max-w-md">
          {/* Logo — mobile only */}
          <div className="flex flex-col items-center mb-8 md:hidden">
            <img src="/images/finproject-logo.png" alt="FinProject" className="w-16 h-16 object-contain mb-3 rounded-xl" style={{ border: "2px solid #ffffff", padding: "3px", background: "#ffffff" }} />
            <span className="text-xl font-bold text-white mb-1">FinProject</span>
            <p className="text-sm text-white/60">Construction Finance Platform</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl p-8">
            <div className="mb-7">
              <h1 className="text-2xl font-bold text-white mb-1">Welcome back</h1>
              <p className="text-sm text-white/60">Sign in to your account to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold tracking-wide uppercase" style={{ color: "#C9A840" }}>Email Address</Label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="h-11 rounded-xl input-dark"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold tracking-wide uppercase" style={{ color: "#C9A840" }}>Password</Label>
                  <Link href="/forgot-password" className="text-xs font-medium transition-colors flex items-center gap-1" style={{ color: "#C9A840" }}>
                    <KeyRound className="w-3 h-3" />
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    type={showPwd ? "text" : "password"}
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-11 rounded-xl input-dark pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={login.isPending}
                className="w-full h-11 rounded-xl font-semibold text-sm border-0 mt-1 text-slate-900 hover:opacity-90"
                style={{ background: "#C9A840" }}
              >
                {login.isPending
                  ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</span>
                  : "Sign In"
                }
              </Button>

              <p className="text-center text-xs text-white/40">
                🔒 Secure login · 256-bit encryption
              </p>
            </form>
          </div>

          <div className="mt-5 text-center text-sm text-white/60">
            Don't have an account?{" "}
            <Link href="/signup" className="font-medium transition-colors" style={{ color: "#C9A840" }}>
              Try a demo or sign up
            </Link>
          </div>

          <div className="mt-4 text-center">
            <Link href="/" className="text-xs text-white/30 hover:text-white/60 transition-colors">
              ← Back to website
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
